'use client';

/**
 * API namespace `adminGeo`.
 * Verbatim extraction from frontend/src/lib/api-client.ts (L9134-L9264).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-2, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch } from '../core/fetch-client';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

export const adminGeo = {
  /**
   * GET /admin/geo-locations — list with optional filters.
   *
   * Query params (all optional):
   *   - type: 'country' | 'state' | 'city'
   *   - parentId: UUID (filter by parent — ej: states of country X)
   *   - status: 'active' | 'inactive'
   *   - page: number (default 1)
   *   - limit: number (max 200, default 50)
   *
   * Returns paginated list with `childrenCount` per entry (for showing
   * "Colombia (32 estados)" in the admin UI).
   */
  async list(params: {
    type?: 'country' | 'state' | 'city';
    parentId?: string;
    status?: 'active' | 'inactive';
    page?: number;
    limit?: number;
  } = {}): Promise<{
    data: Array<{
      geoLocationId: string;
      type: 'country' | 'state' | 'city';
      name: string;
      code: string | null;
      parentId: string | null;
      status: 'active' | 'inactive';
      childrenCount: number;
      createdAt: string;
      updatedAt: string;
    }>;
    total: number;
    page: number;
    limit: number;
  }> {
    const search = new URLSearchParams();
    if (params.type) search.set('type', params.type);
    if (params.parentId) search.set('parentId', params.parentId);
    if (params.status) search.set('status', params.status);
    if (params.page) search.set('page', String(params.page));
    if (params.limit) search.set('limit', String(params.limit));
    const qs = search.toString();
    return apiFetch(`/admin/geo-locations${qs ? `?${qs}` : ''}`);
  },
  /**
   * POST /admin/geo-locations — create a new entry.
   * Requires 2FA (server-side enforce via requireRecent2FA middleware).
   */
  async create(input: {
    type: 'country' | 'state' | 'city';
    name: string;
    code: string | null;
    parentId: string | null;
    status?: 'active' | 'inactive';
  }): Promise<{
    data: {
      geoLocationId: string;
      type: 'country' | 'state' | 'city';
      name: string;
      code: string | null;
      parentId: string | null;
      status: 'active' | 'inactive';
      childrenCount: number;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    return apiFetch('/admin/geo-locations', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  /**
   * PUT /admin/geo-locations/:id — partial update.
   * `type` cannot be changed (server-side validation).
   * Requires 2FA.
   */
  async update(geoLocationId: string, input: {
    name?: string;
    code?: string | null;
    parentId?: string | null;
    status?: 'active' | 'inactive';
  }): Promise<{
    data: {
      geoLocationId: string;
      type: 'country' | 'state' | 'city';
      name: string;
      code: string | null;
      parentId: string | null;
      status: 'active' | 'inactive';
      childrenCount: number;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    return apiFetch(`/admin/geo-locations/${geoLocationId}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },
  /**
   * DELETE /admin/geo-locations/:id — soft-delete (status='inactive').
   * NO hard-delete (preserves referential integrity with existing KYCs
   * that may reference this entry).
   * Requires 2FA.
   */
  async softDelete(geoLocationId: string): Promise<{
    data: {
      geoLocationId: string;
      status: 'inactive';
    };
  }> {
    return apiFetch(`/admin/geo-locations/${geoLocationId}`, {
      method: 'DELETE',
    });
  },
};
